import {skipToken} from '@reduxjs/toolkit/query';
import {Parameter} from 'platform/components';
import {useFormatNumber} from 'platform/locale';

import {isNil, isNotNil} from 'ramda';

import {useGetVehicleCoverPhotoQuery} from '@dms/api/sales';
import {useGetVehicleQuery} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';
import {SnippetProps} from '@dms/shared';

import {Nullish, buildArray, composePath, useNavigate} from 'shared/web';

import {getRecordAuditPairResourceLabel} from '../recordAuditPairResources';

export interface VehicleSnippetResult {
  snippet: SnippetProps | null;
  /** Cover photo of the vehicle, drives the header image. */
  coverImageUrl: string | Nullish;
}

const TEST_ID = 'recordAuditDetail-header-vehicleSnippet';

export function useVehicleSnippet(vehicleId: string | null): VehicleSnippetResult {
  const navigate = useNavigate();
  const formatNumber = useFormatNumber();

  const {data: vehicle, isLoading} = useGetVehicleQuery(
    isNotNil(vehicleId) ? {vehicleId} : skipToken
  );

  // Dedicated cover-photo endpoint keyed by vehicleId only — no participation/permission
  // lookup, so it never hits the `participation/VEHICLE/` route that 404s on an empty id.
  const {data: coverPhoto} = useGetVehicleCoverPhotoQuery(
    isNotNil(vehicleId) ? {vehicleId} : skipToken
  );

  if (isNil(vehicleId)) {
    return {snippet: null, coverImageUrl: null};
  }

  // VIN and licence plate always render with a "No …" placeholder when missing,
  // mirroring the vehicle header (useGetVehicleHeaderInformationParameters); year and
  // mileage are shown only when present. A blank string counts as missing too.
  const vin = vehicle?.vin;
  const registrationPlate = vehicle?.registrationPlate;
  const parameters = buildArray<Parameter>()
    .add(isNotNil(vin) && vin !== '' ? vin : i18n.t('entity.vehicle.notifications.noVIN'))
    .add(
      isNotNil(registrationPlate) && registrationPlate !== ''
        ? registrationPlate
        : i18n.t('entity.vehicle.notifications.noLicencePlate')
    )
    .when(isNotNil(vehicle?.modelYear), vehicle?.modelYear ?? '')
    .when(
      isNotNil(vehicle?.mileage),
      `${formatNumber(vehicle?.mileage ?? 0, 0)} ${i18n.t('general.metric.km')}`
    );

  const snippet: SnippetProps = {
    label: getRecordAuditPairResourceLabel('VEHICLE'),
    title: vehicle?.title ?? (isLoading ? '…' : i18n.t('general.notifications.notFound')),
    onClick: () => navigate(composePath(vehiclesRoutes.detail, {params: {id: vehicleId}})),
    parameters,
    isLoading,
    'data-testid': TEST_ID,
  };

  return {snippet, coverImageUrl: coverPhoto?.uri};
}
